import type { TInstalledCommand } from '@/atoms/slash-commands'
import { nip19 } from '@nostr/tools'

/**
 * NIP-XX (Commands) kind 31992 command definition.
 * See https://github.com/coracle-social/nips/blob/9726fe5a8a750ccfe03722e0a17c70a21068a1c7/CD.md
 */
export const COMMAND_DEFINITION_KIND = 31992

export type TCommandArg = {
  name: string
  type: string
  required: boolean
  label?: string
  choices: string[]
}

export type TCommandDefinition = {
  d: string
  command?: string
  title?: string
  description?: string
  args: TCommandArg[]
  scopes: string[]
  ignores: string[]
}

export function parseCommandDefinition(tags: string[][]): TCommandDefinition {
  const definition: TCommandDefinition = { d: '', args: [], scopes: [], ignores: [] }

  for (const tag of tags) {
    switch (tag[0]) {
      case 'd':
        definition.d = tag[1] ?? ''
        break
      case 'command':
        definition.command = tag[1]
        break
      case 'title':
        definition.title = tag[1]
        break
      case 'description':
        definition.description = tag[1]
        break
      case 'arg':
        definition.args.push({
          name: tag[1] ?? '',
          type: tag[2] ?? 'text',
          required: tag[3] === 'required',
          label: tag[4],
          choices: tag.slice(5)
        })
        break
      case 's':
        if (tag[1]) definition.scopes.push(tag[1])
        break
      case 'ignore':
        if (tag[1]) definition.ignores.push(tag[1])
        break
    }
  }

  return definition
}

/**
 * Human-readable summary of the `s` scopes, e.g. "kind 1, kind 9, author npub1…".
 * All scope types are conjunctive so this is purely presentational.
 */
export function describeScopes(scopes: string[]): string[] {
  const descriptions: string[] = []

  for (const scope of scopes) {
    if (scope.startsWith('kind:')) {
      descriptions.push(scope.replace('kind:', 'kind '))
    } else if (scope.startsWith('relay:')) {
      descriptions.push(scope.replace('relay:', ''))
    } else if (scope.startsWith('author:')) {
      const value = scope.slice('author:'.length)
      try {
        descriptions.push(nip19.npubEncode(value))
      } catch {
        descriptions.push(scope)
      }
    } else if (scope.startsWith('tag:')) {
      descriptions.push(scope.replace('tag:', '#'))
    } else {
      descriptions.push(scope)
    }
  }

  return descriptions
}

/** A command is installable if its `author:` scopes (if any) only target the user. */
export function isCommandInstallableForUser(definition: TCommandDefinition, pubkey: string) {
  return definition.scopes
    .filter((scope) => scope.startsWith('author:'))
    .every((scope) => scope.slice('author:'.length) === pubkey)
}

/**
 * Whether a command can be invoked when creating an event with the given kind
 * and tags: `kind:` scopes must match the target kind exactly and `tag:`
 * scopes must have a matching tag in the target event.
 */
export function commandAppliesToEvent(command: TInstalledCommand, kind: number, tags: string[][]) {
  const scopes = command.scopes

  if (scopes.kind && !scopes.kind.includes(kind)) return false

  for (const tagName of scopes.tagPresence ?? []) {
    if (!tags.some((tag) => tag[0] === tagName)) return false
  }

  for (const [tagName, values] of Object.entries(scopes.tagValue ?? {})) {
    for (const tagValue of values) {
      if (!tags.some((tag) => tag[0] === tagName && tag[1] === tagValue)) return false
    }
  }

  return true
}

/** Relay URLs required by the command's `relay:` scopes. */
export function getCommandRelayUrls(definition: TCommandDefinition): string[] {
  return definition.scopes
    .filter((scope) => scope.startsWith('relay:'))
    .map((scope) => scope.slice('relay:'.length))
}
